import { r as registerInstance, c as createEvent, h } from './core-6f498fd9.js';
import { C as CsmOverlayType, a as CsmOverlayScrollStrategy, c as csmOverlayBlockScrollId } from './csm-overlay.constants-8ff0efb3.js';
var CsmOverlay = /** @class */ (function () {
    function CsmOverlay(hostRef) {
        registerInstance(this, hostRef);
        /**
         * Whether the overlay should be shown or not. It will also set scroll listeners when needed.
         */
        this.active = false;
        /**
         * Set the type of background.
         */
        this.type = CsmOverlayType.Light;
        /**
         * There are 3 scroll strategies (e.g. what happens when the user scrolls on the overlay) available:<br/>
         * - None: no special handling, let the scroll be handled normally (generally meaning that the body will scroll):<br/>
         * - Close: automatically close the overlay.<br/>
         *   *Important note:* If the overlay's content has a scrollable content,
         *   you might have to manually handle a blocking behaviour for this content. Fortunately it's simple: just add
         *   `overscroll-behavior: none` on the scrolling container inside the overlay to disable the "bubbling" of the scroll.<br/>
         * - Block: block the scroll on the `body` element.
         */
        this.scrollStrategy = CsmOverlayScrollStrategy.None;
        /**
         * Sets whether the overlay should close on ESC keyboard press
         */
        this.dismissOnEscape = false;
        this.handleScroll = this.handleScroll.bind(this);
        this.addScrollBlockerStyle();
        this.csmDismiss = createEvent(this, "csmDismiss", 7);
        this.csmOverlayClose = createEvent(this, "csmOverlayClose", 7);
    }
    CsmOverlay.prototype.componentDidLoad = function () {
        this.setScrollBlocker();
    };
    CsmOverlay.prototype.componentDidUnload = function () {
        this.removeScrollListener();
        this.removeScrollBlocker();
    };
    CsmOverlay.prototype.toggleScrollBehavior = function () {
        if (this.active) {
            this.setScrollListener();
            this.setScrollBlocker();
        }
        else {
            this.removeScrollListener();
            this.removeScrollBlocker();
            this.csmDismiss.emit();
            this.csmOverlayClose.emit();
        }
    };
    CsmOverlay.prototype.handleKeyDown = function (event) {
        if (this.dismissOnEscape && event.key === 'Escape') {
            this.active = false;
        }
    };
    CsmOverlay.prototype.setScrollBlocker = function () {
        if (this.scrollStrategy === CsmOverlayScrollStrategy.Block && this.active) {
            document.body.classList.add(csmOverlayBlockScrollId);
        }
    };
    CsmOverlay.prototype.handleScroll = function () {
        switch (this.scrollStrategy) {
            case CsmOverlayScrollStrategy.Close:
                this.active = false;
                return;
            default:
                return;
        }
    };
    CsmOverlay.prototype.setScrollListener = function () {
        document.addEventListener('scroll', this.handleScroll, { passive: true });
    };
    CsmOverlay.prototype.removeScrollListener = function () {
        document.removeEventListener('scroll', this.handleScroll);
    };
    CsmOverlay.prototype.removeScrollBlocker = function () {
        document.body.classList.remove(csmOverlayBlockScrollId);
    };
    CsmOverlay.prototype.addScrollBlockerStyle = function () {
        if (!document.head.querySelector("[" + csmOverlayBlockScrollId + "]")) {
            var style = document.createElement('style');
            style.innerText = "." + csmOverlayBlockScrollId + " { overflow: hidden; }";
            style.setAttribute(csmOverlayBlockScrollId, '');
            document.head.appendChild(style);
        }
    };
    CsmOverlay.prototype.render = function () {
        return h("slot", null);
    };
    Object.defineProperty(CsmOverlay, "watchers", {
        get: function () {
            return {
                "active": ["toggleScrollBehavior"]
            };
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CsmOverlay, "style", {
        get: function () { return "\@font-face{font-family:Proxima Nova;font-style:normal;font-weight:400;src:url(../assets/fonts/ProximaNova-Regular.woff2) format(\"woff2\"),url(../assets/fonts/ProximaNova-Regular.woff) format(\"woff\")}\@font-face{font-family:Proxima Nova;font-style:normal;font-weight:600;src:url(../assets/fonts/ProximaNova-Semibold.woff2) format(\"woff2\"),url(../assets/fonts/ProximaNova-Semibold.woff) format(\"woff\")}\@font-face{font-family:Proxima Nova;font-style:normal;font-weight:700;src:url(../assets/fonts/ProximaNova-Semibold.woff2) format(\"woff2\"),url(../assets/fonts/ProximaNova-Semibold.woff) format(\"woff\")}\@font-face{font-family:Proxima Nova;font-style:normal;font-weight:900;src:url(../assets/fonts/ProximaNova-Black.woff2) format(\"woff2\"),url(../assets/fonts/ProximaNova-Black.woff) format(\"woff\")}\@font-face{font-family:CS-FontIcon-v19;src:url(../assets/icons/CS-FontIcon-v19.eot?cz1hbb);src:url(../assets/icons/CS-FontIcon-v19.eot?cz1hbb#iefix) format(\"embedded-opentype\"),url(../assets/icons/CS-FontIcon-v19.ttf?cz1hbb) format(\"truetype\"),url(../assets/icons/CS-FontIcon-v19.woff?cz1hbb) format(\"woff\"),url(../assets/icons/CS-FontIcon-v19.svg?cz1hbb#CS-FontIcon-v18) format(\"svg\");font-weight:400;font-style:normal;font-display:block}:root{font-family:Proxima Nova,sans-serif}*,:after,:before{-webkit-box-sizing:border-box;box-sizing:border-box;font-family:inherit}:host{--overlay-z-index:10;position:fixed;top:0;left:0;display:block;height:100vh;width:100vw;max-width:100%;opacity:0;pointer-events:none;background-color:hsla(0,0%,100%,.25);-webkit-transition:background-color .2s cubic-bezier(.4,0,.2,1),opacity .2s cubic-bezier(.4,0,.2,1);transition:background-color .2s cubic-bezier(.4,0,.2,1),opacity .2s cubic-bezier(.4,0,.2,1);z-index:var(--overlay-z-index)}:host([active]:not([active=false])){opacity:1;pointer-events:auto}:host([type=light]){background-color:hsla(0,0%,100%,.25)}:host([type=light-strong]){background-color:hsla(0,0%,100%,.5)}:host([type=dark]){background-color:rgba(0,0,0,.25)}:host([type=dark-strong]){background-color:rgba(0,0,0,.5)}"; },
        enumerable: true,
        configurable: true
    });
    return CsmOverlay;
}());
export { CsmOverlay as csm_overlay };
